<template>
  <div class="lead-communication-timeline">
    <div v-if="loading && !items.length" class="lead-communication-timeline__loading" aria-label="Loading communications">
      <span v-for="index in 3" :key="index"></span>
    </div>
    <div v-else-if="error && !items.length" class="lead-communication-timeline__state" role="alert">
      <p>Unable to load communication timeline.</p>
      <button type="button" :disabled="loading" @click="$emit('retry')">
        {{ loading ? 'Loading...' : 'Retry' }}
      </button>
    </div>
    <template v-else-if="groups.length">
      <article
        v-for="group in groups"
        :key="group.key"
        class="lead-communication-timeline__period"
      >
        <div class="lead-communication-timeline__rail">
          <span :style="{ backgroundColor: group.stage.color }"></span>
        </div>
        <div class="lead-communication-timeline__content">
          <header>
            <AppStatusBadge
              design="new"
              :color="group.stage.color"
              :label="group.stage.name"
            />
            <small>{{ group.items.length }} {{ group.items.length === 1 ? 'event' : 'events' }}</small>
          </header>
          <ul>
            <li
              v-for="communication in group.items"
              :key="communication.id"
              :class="communicationToneClass(communication)"
            >
              <i :class="iconClass(communication)" aria-hidden="true"></i>
              <div>
                <strong>{{ communicationTitle(communication) }}</strong>
                <p v-if="communication.report">{{ communication.report }}</p>
                <small>
                  <span v-if="communication.direction">
                    {{ valueName(communication.direction) }}
                  </span>
                  <span v-if="communication.source">via {{ communication.source.name }}</span>
                  <LeadCommunicationRating
                    :rating="communication.first_reply_rating"
                    :communication-id="communication.id"
                  />
                  <LeadCommunicationDeliveryStatus
                    :status="communication.delivery_status"
                    :communication-methods="communication.methods"
                  />
                  <time
                    v-tooltip="{ content: formatExactDate(communication.date), placement: 'top', offset: 8 }"
                  >
                    {{ formatRelativeDate(communication.date) }}
                  </time>
                </small>
              </div>
            </li>
          </ul>
        </div>
      </article>
      <button
        v-if="hasMore"
        class="lead-communication-timeline__more"
        type="button"
        :disabled="loadingMore"
        @click="$emit('load-more')"
      >
        {{ loadingMore ? 'Loading...' : 'Show older communications' }}
      </button>
      <p v-if="error" class="lead-communication-timeline__append-error">
        Loading failed.
        <button type="button" @click="$emit('load-more')">Retry</button>
      </p>
    </template>
    <p v-else class="lead-communication-timeline__empty">No communications in this view</p>
  </div>
</template>

<script>
import LeadCommunicationDeliveryStatus from '@/views/crm/task-tracker/LeadCommunicationDeliveryStatus.vue'
import LeadCommunicationRating from '@/views/crm/task-tracker/LeadCommunicationRating.vue'
import { formatExactDate, formatRelativeDate } from '@/views/crm/task-tracker/leadFlowDate'

export default {
  name: 'LeadCommunicationTimeline',
  components: {
    LeadCommunicationDeliveryStatus,
    LeadCommunicationRating
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    error: {
      default: null
    },
    hasMore: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    groups() {
      const groups = []
      const groupByKey = new Map()
      this.items.forEach(communication => {
        const periodId = communication.stage_period_id
        const withoutStage = periodId === null || typeof periodId === 'undefined'
        let key = `stage-period:${periodId}`
        if (withoutStage) key = 'without-stage'
        if (!groupByKey.has(key)) {
          let stage = communication.stage
          if (withoutStage || !stage) {
            stage = { code: 'without_stage', name: 'Without stage', color: '#879392' }
          }
          const group = {
            key,
            stage,
            items: []
          }
          groupByKey.set(key, group)
          groups.push(group)
        }
        groupByKey.get(key).items.push(communication)
      })
      return groups
    }
  },
  methods: {
    formatExactDate,
    formatRelativeDate,
    valueName(value) {
      return value && (value.name || value.code) ? (value.name || value.code) : ''
    },
    communicationTitle(communication) {
      if (communication.subject) return communication.subject
      const methods = communication.methods || []
      if (methods.length) return methods.map(method => method.name).filter(Boolean).join(' / ')
      if (communication.source && communication.source.name) return communication.source.name
      return 'Communication'
    },
    iconClass(communication) {
      const sourceCode = communication.source && communication.source.code
      const methods = (communication.methods || []).map(method => {
        return String(method.code || method.id || method.name || '').toLowerCase()
      })
      if (sourceCode === 'gmail' || methods.some(code => code.includes('email'))) {
        return 'lead-communication-timeline__icon--email'
      }
      if (sourceCode === 'trengo' || methods.some(code => code.includes('whatsapp') || code === 'wa')) {
        return 'lead-communication-timeline__icon--chat'
      }
      if (methods.some(code => code.includes('phone') || code.includes('call'))) {
        return 'lead-communication-timeline__icon--phone'
      }
      if (sourceCode === 'manual') return 'lead-communication-timeline__icon--manual'
      return 'lead-communication-timeline__icon--other'
    },
    communicationToneClass(communication) {
      return this.iconClass(communication).replace('__icon--', '__item--')
    }
  }
}
</script>

<style scoped>
.lead-communication-timeline {
  min-width: 0;
}

.lead-communication-timeline__period {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
}

.lead-communication-timeline__rail {
  position: relative;
  display: flex;
  justify-content: center;
}

.lead-communication-timeline__rail::after {
  position: absolute;
  top: 14px;
  bottom: -15px;
  width: 1px;
  background: #dfe4dd;
  content: '';
}

.lead-communication-timeline__period:last-of-type .lead-communication-timeline__rail::after {
  display: none;
}

.lead-communication-timeline__rail span {
  z-index: 1;
  width: 9px;
  height: 9px;
  margin-top: 8px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #d7ddd5;
}

.lead-communication-timeline__content {
  min-width: 0;
  padding: 0 0 18px 8px;
}

.lead-communication-timeline__content > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.lead-communication-timeline__content > header > span {
  padding: 3px 7px;
  border: 1px solid;
  border-radius: 11px;
  font-size: 9px;
  font-weight: 700;
}

.lead-communication-timeline__content > header small {
  color: #8b9795;
  font-size: 9px;
}

.lead-communication-timeline ul {
  display: grid;
  gap: 7px;
  margin: 9px 0 0;
  padding: 0;
  list-style: none;
}

.lead-communication-timeline li {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 8px;
  padding: 8px;
  border: 1px solid #e1e6df;
  border-radius: 5px;
  background: #fbfcfa;
}

.lead-communication-timeline li > i {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: #eef2ed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 13px;
}

.lead-communication-timeline__icon--email {
  background-image: url('~@/assets/img/email-icon.svg');
}

.lead-communication-timeline__icon--chat {
  background-image: url('~@/assets/img/update-small-icon.svg');
}

.lead-communication-timeline__icon--phone {
  background-image: url('~@/assets/img/phone-icon.svg');
}

.lead-communication-timeline__icon--manual,
.lead-communication-timeline__icon--other {
  background-image: url('~@/assets/img/update-small-icon.svg');
}

.lead-communication-timeline li strong {
  color: #3a4d4e;
  font-size: 10px;
}

.lead-communication-timeline li p {
  margin: 4px 0 0;
  color: #536364;
  font-size: 10px;
  line-height: 14px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.lead-communication-timeline li small {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 7px;
  margin-top: 5px;
  color: #8b9694;
  font-size: 8px;
}

.lead-communication-timeline__more {
  width: 100%;
  min-height: 30px;
  border: 1px solid #d7e2cb;
  border-radius: 5px;
  background: #f8fbf5;
  color: #5c8d20;
  font-size: 9px;
  font-weight: 700;
  cursor: pointer;
}

.lead-communication-timeline__empty,
.lead-communication-timeline__state,
.lead-communication-timeline__append-error {
  margin: 10px 0;
  color: #8a9695;
  font-size: 10px;
}

.lead-communication-timeline__state button,
.lead-communication-timeline__append-error button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #55940a;
  font-weight: 700;
  cursor: pointer;
}

.lead-communication-timeline__state {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.lead-communication-timeline__state p {
  margin: 0;
}

.lead-communication-timeline__state button {
  min-height: 22px;
  padding: 0 8px;
  border: 1px solid #c9d6ac;
  border-radius: 4px;
  background: #fff;
  font-size: 9px;
  transition: background-color .15s ease, border-color .15s ease;
}

.lead-communication-timeline__state button:hover:not(:disabled),
.lead-communication-timeline__state button:focus-visible:not(:disabled) {
  border-color: #aac27b;
  background: rgba(142, 185, 81, .14);
}

.lead-communication-timeline__state button:disabled {
  cursor: wait;
  opacity: .65;
}

.lead-communication-timeline__append-error {
  color: #b85049;
  text-align: center;
}

.lead-communication-timeline__loading {
  display: grid;
  gap: 8px;
}

.lead-communication-timeline__loading span {
  height: 68px;
  border-radius: 5px;
  background: #eef1ec;
}
</style>
